import React, { useId } from "react";
import {
  ExclamationTriangleIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useTheme } from "../../contexts/ThemeContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { Modal } from "./Modal";
import { LoadingSpinner } from "./LoadingSpinner";

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "danger" | "warning" | "info";
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const variantStyles = {
  danger: {
    icon: TrashIcon,
    iconColor: { dark: "text-red-400", light: "text-red-600" },
    confirmBtn: "bg-red-600 hover:bg-red-700 focus:ring-red-500",
  },
  warning: {
    icon: ExclamationTriangleIcon,
    iconColor: { dark: "text-yellow-400", light: "text-yellow-600" },
    confirmBtn: "bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500",
  },
  info: {
    icon: ExclamationTriangleIcon,
    iconColor: { dark: "text-blue-400", light: "text-blue-600" },
    confirmBtn: "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500",
  },
};

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  variant = "danger",
  loading = false,
  onConfirm,
  onCancel,
}) => {
  const { theme } = useTheme();
  const { t } = useLanguage();
  const isDark = theme === "dark";
  const style = variantStyles[variant];
  const Icon = style.icon;

  // Stable id to wire aria-labelledby on the dialog to the title <h3>.
  const titleId = useId();

  return (
    <Modal
      open={open}
      onClose={onCancel}
      closeOnEscape={!loading}
      closeOnBackdrop={!loading}
      labelledById={titleId}
      className={`w-full max-w-sm mx-4 rounded-lg shadow-elevated border ${
        isDark
          ? "bg-gray-900 border-gray-700/60"
          : "bg-white border-gray-200/80"
      }`}
    >
      <button
        onClick={onCancel}
        disabled={loading}
        // Idiomatic AT name for an X: "Close", not the Cancel button's own
        // (often task-specific) label. Behavior is unchanged — it still
        // calls onCancel, the same action as the text Cancel button below;
        // only the accessible name of this icon-only control changes.
        aria-label={t("common.close")}
        className={`absolute top-3 right-3 p-1 rounded-md transition-colors duration-200 disabled:opacity-50 ${
          isDark
            ? "text-gray-500 hover:text-white hover:bg-gray-800/60"
            : "text-gray-400 hover:text-gray-900 hover:bg-gray-100"
        }`}
      >
        <XMarkIcon className="w-4 h-4" />
      </button>

      <div className="p-5">
        <h3
          id={titleId}
          className={`flex items-center gap-2 text-lg font-semibold mb-2 ${
            isDark ? "text-white" : "text-gray-900"
          }`}
        >
          <Icon
            className={`w-5 h-5 shrink-0 ${isDark ? style.iconColor.dark : style.iconColor.light}`}
          />
          {title}
        </h3>

        <p
          className={`text-sm mb-3 ${
            isDark ? "text-gray-300" : "text-gray-600"
          }`}
        >
          {message}
        </p>

        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className={`flex-1 px-3.5 py-2 rounded-md text-sm font-medium transition-colors duration-200 disabled:opacity-50 ${
              isDark
                ? "bg-gray-800 hover:bg-gray-700 text-white ring-1 ring-inset ring-gray-700/50"
                : "bg-gray-100 hover:bg-gray-200 text-gray-900 ring-1 ring-inset ring-gray-200"
            }`}
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`flex-1 px-3.5 py-2 rounded-md text-sm font-medium text-white transition-colors duration-200 disabled:opacity-50 shadow-subtle focus:outline-hidden focus:ring-2 focus:ring-offset-2 ${
              isDark
                ? "focus:ring-offset-gray-900"
                : "focus:ring-offset-white"
            } ${style.confirmBtn}`}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                {/* White spinner to match the colored confirm button's text
                    (overrides LoadingSpinner's default blue, like the prior
                    currentColor SVG). */}
                <LoadingSpinner
                  size="sm"
                  className="border-white/30! border-t-white!"
                />
                <span>{confirmLabel}</span>
              </span>
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};
